import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Mail, Linkedin } from 'lucide-react';
import AppLogo from '@/components/AppLogo';
import AuthorAvatar from '@/components/AuthorAvatar';
import SiteFooter from '@/components/SiteFooter';
import { AUTHOR, ORG } from '@/config/authorProfile';

export default function MastheadPage() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <header className="container mx-auto px-4 sm:px-6 py-6 max-w-4xl">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
            <AppLogo size={36} />
            <p className="heading text-sm font-semibold tracking-tight text-slate-900">
              How Busy Is My Airport
            </p>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-slate-600 hover:text-blue-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 pb-16 max-w-3xl">
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="pt-6 pb-8"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600 mb-4">
            Masthead
          </p>
          <h1 className="heading text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-4">
            Who runs this site
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-2xl">
            {ORG.name} is a small, independent travel-data publication.
            {' '}Everyone who writes, edits, or ships code here is listed below with
            a real name and a reachable email.
          </p>
        </motion.section>

        <Card className="border-slate-200 bg-white shadow-sm mb-6" data-testid="masthead-editor">
          <CardContent className="p-6 sm:p-7">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-4">
              Editorial
            </p>
            <div className="flex items-start gap-4">
              <AuthorAvatar size={72} />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-900 text-lg">{AUTHOR.name}</p>
                <p className="text-sm text-slate-500 mb-2">
                  {AUTHOR.role} · {AUTHOR.location}
                </p>
                <p className="text-sm text-slate-700 leading-relaxed mb-3">
                  {AUTHOR.headline} Owns the site end-to-end: editorial,
                  data model, product, and reader replies.
                </p>
                <div className="flex flex-wrap gap-3 text-xs">
                  <a
                    href={`mailto:${AUTHOR.email}`}
                    className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                  >
                    <Mail className="w-3 h-3" /> {AUTHOR.email}
                  </a>
                  {AUTHOR.linkedin && (
                    <a
                      href={AUTHOR.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                    >
                      <Linkedin className="w-3 h-3" /> LinkedIn
                    </a>
                  )}
                  <Link
                    to={`/authors/${AUTHOR.slug}`}
                    className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                  >
                    Author page →
                  </Link>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-white shadow-sm mb-6">
          <CardContent className="p-6 sm:p-7">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3">
              Ownership
            </p>
            <p className="text-sm text-slate-700 leading-relaxed">
              {ORG.name} is operated by <strong>{ORG.legalOwner}</strong>,
              based in {ORG.mailingAddress.city}, {ORG.mailingAddress.region}.
              Founded {ORG.founded}. Bootstrapped and independently owned — no
              outside investors, no parent company, no editorial oversight from
              any airline, lounge network, or credit-card issuer.
            </p>
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-white shadow-sm mb-6">
          <CardContent className="p-6 sm:p-7">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3">
              Data partners
            </p>
            <ul className="text-sm text-slate-700 space-y-2">
              <li>
                <strong>FlightAware AeroAPI</strong> — primary flight schedule
                and aircraft data feed. Paid commercial license.
              </li>
              <li>
                <strong>FAA ATCSCC</strong> — public air-traffic-control delay reports.
              </li>
              <li>
                <strong>Individual airport authorities</strong> — terminal maps,
                published TSA wait times, parking rates.
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-white shadow-sm">
          <CardContent className="p-6 sm:p-7">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3">
              How we make money
            </p>
            <p className="text-sm text-slate-700 leading-relaxed mb-3">
              Reader-supported and affiliate revenue only. When we link to a
              parking marketplace, a credit card, or a lounge program, we may
              earn a commission if you book or apply. That relationship is
              disclosed on every affected page and covered in full in our{' '}
              <Link to="/affiliate-disclosure" className="text-blue-600 hover:underline">
                affiliate disclosure
              </Link>. We do not accept paid placements or sponsored posts.
            </p>
          </CardContent>
        </Card>
      </main>

      <SiteFooter />
    </div>
  );
}
